import axios from 'axios';
import { create } from 'zustand';

import { isCanonicalHost, pathIsReserved } from './host.js';

/**
 * Resolves, once per page load, whether the SPA is running on a tenant's custom
 * domain and what it should serve. Host-derived (not session state), so it is
 * NOT persisted. The backend `GET /public/site-context` resolves the tenant from
 * the request Host header (works when the SPA + API are same-origin behind the
 * proxy, which is the deployment model for custom domains).
 */
export type SiteContextStatus =
  | 'idle'
  | 'resolving'
  | 'canonical' // platform host / reserved path / feature off → render the app as-is
  | 'app'
  | 'storefront'
  | 'unconfigured'; // a custom host that isn't a known/verified tenant domain

export interface SiteTenant {
  slug: string;
  name: string;
  tagline: string | null;
  logoUrl: string | null;
  defaultLocale: string;
  theme: { themeColor: string | null; secondaryColor: string | null } | null;
}

interface SiteContextState {
  status: SiteContextStatus;
  kind: 'app' | 'storefront' | null;
  tenant: SiteTenant | null;
  resolve(): Promise<void>;
}

const rawBase = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Decide synchronously at store creation so the common case (platform host,
// reserved path, or feature off) renders the app immediately with no spinner
// flash. Only a real custom-domain hit starts as 'idle' and triggers a fetch.
const initialStatus: SiteContextStatus =
  isCanonicalHost() || pathIsReserved() ? 'canonical' : 'idle';

export const useSiteContextStore = create<SiteContextState>((set, get) => ({
  status: initialStatus,
  kind: null,
  tenant: null,
  resolve: async () => {
    if (get().status !== 'idle') return; // resolve once; already settled otherwise
    set({ status: 'resolving' });
    try {
      const r = await axios.get<{ kind: 'app' | 'storefront'; tenant: SiteTenant }>(
        `${rawBase}/public/site-context`,
        { headers: { 'Content-Type': 'application/json' } },
      );
      set({ status: r.data.kind, kind: r.data.kind, tenant: r.data.tenant });
    } catch {
      set({ status: 'unconfigured' });
    }
  },
}));
